import { normalizedNoteTitle } from './owner-notes';

/**
 * Behavior contract for web and later iOS. Do not share UI code.
 *
 * "Discuss with AI" (help text: "Send to Main Command") applies only to the
 * note the owner currently has selected. It stages that note into the existing
 * Main Command composer and opens Main Command. Draft text is the normalized
 * title, a blank line, then the body. An empty title becomes "Untitled page"
 * and is capped at 120 characters, matching the notebook. An empty body stages
 * the title alone. The draft is clipped to 16000 characters (the Main Command
 * composer limit) and the chip records that clip.
 *
 * Nothing is sent. Recipients are not changed. The owner can edit the draft,
 * choose the usual recipients, and press Send. A visible chip previews the
 * staged note so the share is obvious before Send. Other notes stay excluded.
 *
 * Notes must not be added to Overseer, Observer, MCP, the shared timeline, or
 * any automatic context. The only share is the draft the owner explicitly
 * sends. Changing organization drops a staged note so it cannot be sent from
 * another organization.
 */
export const NOTE_HANDOFF_COMPOSER_LIMIT = 16_000;

export interface NoteHandoffNote {
  readonly id: string;
  readonly title: string;
  readonly body: string;
}

export interface NoteHandoffChip {
  readonly noteId: string;
  readonly title: string;
  /** Body text included in the draft, or "(empty note)" when the body is empty. */
  readonly preview: string;
  readonly truncated: boolean;
}

export interface NoteHandoffStage {
  readonly text: string;
  readonly chip: NoteHandoffChip;
  readonly section: 'control';
  /** Staging never dispatches. The owner sends from Main Command. */
  readonly autoSend: false;
  /** Existing Main Command recipients stay selected. */
  readonly recipientOverride: null;
}

export function stageSelectedNoteHandoff(note: NoteHandoffNote): NoteHandoffStage {
  const title = normalizedNoteTitle(note.title).slice(0, NOTE_HANDOFF_COMPOSER_LIMIT);
  const separator = note.body.length > 0 ? '\n\n' : '';
  const room = NOTE_HANDOFF_COMPOSER_LIMIT - title.length - separator.length;
  const truncated = note.body.length > room;
  const includedBody = truncated ? note.body.slice(0, Math.max(0, room)) : note.body;
  const text = includedBody.length > 0 ? `${title}${separator}${includedBody}` : title;
  return {
    text,
    chip: {
      noteId: note.id,
      title,
      preview: includedBody.length > 0 ? includedBody : '(empty note)',
      truncated,
    },
    section: 'control',
    autoSend: false,
    recipientOverride: null,
  };
}
